import { Head, Link } from '@inertiajs/react'
import { AreaType } from './types'
import {Activity, ArrowLeft, Briefcase, DollarSign, Edit, MapPin, Users} from 'lucide-react'

interface ShowProps {
  area: AreaType
}

export default function Show({ area }: ShowProps) {
  const categoryCounts = area.tax_payer_counts_by_category ?? []

  return (
    <>
      <Head title={`Area #${area.id}`} />
      <div className="flex flex-col gap-6 p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/system/areas">
              <button className="rounded-lg border border-border p-2 text-foreground hover:bg-secondary transition-colors">
                <ArrowLeft className="h-5 w-5" />
              </button>
            </Link>
            <div>
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-3">
                  <MapPin className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">{area.name}</h1>
                  <p className="text-sm text-muted-foreground">{area.code}</p>
                </div>
              </div>
            </div>
          </div>
          <Link href={`/admin/areas/${area.id}/edit`}>
            <button className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
              <Edit className="h-4 w-4" />
              Editer la zone
            </button>
          </Link>
        </div>

        {/* Overview Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Taxpayers</p>
                <p className="mt-2 text-3xl font-bold text-foreground">{area.tax_payer_count ?? 0}</p>
              </div>
              <Users className="h-8 w-8 text-info" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Collections</p>
                <p className="mt-2 text-3xl font-bold text-success">0 FCFA</p>
              </div>
              <DollarSign className="h-8 w-8 text-success" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Assigned Agent</p>
                <p className="mt-2 font-medium text-foreground">Open</p>
              </div>
              <Briefcase className="h-8 w-8 text-warning" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Status</p>
                <p className="mt-2 text-lg font-semibold">
                  <span className="inline-flex rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">{area.status}</span>
                </p>
              </div>
              <Activity className="h-8 w-8 text-success" />
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* Area Information */}
          <div className="rounded-lg border border-border bg-card p-6 md:col-span-2">
            <h3 className="text-lg font-semibold text-foreground mb-4">Area Information</h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Description</p>
                <p className="mt-1 text-foreground">{area.description}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Assigned Agent</p>
                {/*<p className="mt-1 text-foreground">{areaDetails.agent} (ID: {areaDetails.agentId})</p>*/}
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Created</p>
                <p className="mt-1 text-foreground">{area.created_at}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Last Updated</p>
                {/*<p className="mt-1 text-foreground">{areaDetails.lastUpdated}</p>*/}
              </div>
            </div>
          </div>

          {/* Geographic Boundaries */}
          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Geographic Boundaries</h3>
            <div className="space-y-3">
              <div className="rounded-lg border border-border/50 bg-secondary p-3">
                <p className="text-xs text-muted-foreground">North</p>
                <p className="font-mono text-sm text-foreground">0.0</p>
              </div>
              <div className="rounded-lg border border-border/50 bg-secondary p-3">
                <p className="text-xs text-muted-foreground">South</p>
                <p className="font-mono text-sm text-foreground">0.0</p>
              </div>
              <div className="rounded-lg border border-border/50 bg-secondary p-3">
                <p className="text-xs text-muted-foreground">East</p>
                <p className="font-mono text-sm text-foreground">0.0</p>
              </div>
              <div className="rounded-lg border border-border/50 bg-secondary p-3">
                <p className="text-xs text-muted-foreground">West</p>
                <p className="font-mono text-sm text-foreground">0.0</p>
              </div>
            </div>
          </div>
        </div>

        {/* Taxpayers by Category */}
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Taxpayers by category</h2>
              <p className="text-sm text-muted-foreground">Breakdown for this area.</p>
            </div>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {categoryCounts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No taxpayers recorded in this area yet.</p>
            ) : (
              categoryCounts.map((category) => (
                <div key={category.id} className="rounded-lg border border-border/60 bg-background p-4">
                  <p className="text-sm font-medium text-muted-foreground">{category.name}</p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">{category.count}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

    </>
  )
}
